<script setup lang="ts">
import { buildMyPageLink } from '@metapic-apps/shared'
import { computed, ref } from 'vue'

import { useUpdateCreator } from '@/api'
import { useUpdateIdentityRequest } from '@/api/identity.api'
import {
  CreatorDetailGeneralSection,
  CreatorDetailPaymentSection,
  CreatorDetailSocialsDrawer,
  CreatorDetailTaxSection,
  EditClientModal,
  EditFieldModal,
  EditRevenueTierModal,
} from '@/components/CreatorDetail'
import { config, isValidPhone, notification } from '@/lib/helpers'
import type { CreatorDetail, CreatorExtraInfo } from '@/lib/types/creator-detail.types'
import { BoxComponent, FlexComponent, InputComponent, TagComponent, TypographyComponent } from '@/ui-library'

type InfoSection = 'general' | 'payment' | 'tax'

const props = defineProps<{
  creator: CreatorDetail
  extraInfo: CreatorExtraInfo | null
}>()

const myPage = computed(() => buildMyPageLink(config.myPagePublicUrl, props.extraInfo?.myPageSlug))

const emits = defineEmits<{
  refresh: []
}>()

const activeSection = ref<InfoSection>('general')

const sections: { key: InfoSection; label: string }[] = [
  { key: 'general', label: 'General' },
  { key: 'payment', label: 'Payment' },
  { key: 'tax', label: 'Tax info' },
]

const { updateCreator, isLoading: isUpdating } = useUpdateCreator()
const { updateIdentity, isLoading: isIdentityUpdating, error: identityError } = useUpdateIdentityRequest(props.creator.id)

const isEditEmailOpen = ref(false)
const editEmailValue = ref('')

const isEditUsernameOpen = ref(false)
const editUsernameValue = ref('')

const isEditPhoneOpen = ref(false)
const editPhoneValue = ref('')
const phoneValidationError = ref('')

const isEditClientOpen = ref(false)
const isEditTierOpen = ref(false)

const isSocialsDrawerOpen = ref(false)

const openEditUsername = () => {
  editUsernameValue.value = props.creator.username
  isEditUsernameOpen.value = true
}

const saveUsername = async () => {
  const username = editUsernameValue.value.trim()
  if (!username) {
    return
  }
  const success = await updateCreator(props.creator.id, { username })
  if (!success) {
    return
  }
  isEditUsernameOpen.value = false
  notification.success('Username updated successfully')
  emits('refresh')
}

const openEditEmail = () => {
  editEmailValue.value = props.creator.email
  isEditEmailOpen.value = true
}

const saveEmail = async () => {
  await updateIdentity({ email: editEmailValue.value.trim(), phone: props.creator.phone ?? undefined })
  if (identityError.value) {
    return
  }
  isEditEmailOpen.value = false
  notification.success('Email updated successfully')
  emits('refresh')
}

const openEditPhone = () => {
  editPhoneValue.value = props.creator.phone ?? ''
  phoneValidationError.value = ''
  isEditPhoneOpen.value = true
}

const savePhone = async () => {
  const phone = editPhoneValue.value.trim()
  if (phone && !isValidPhone(phone)) {
    phoneValidationError.value = 'Enter a valid phone number in E.164 format (e.g. +46701234567)'
    return
  }
  phoneValidationError.value = ''
  await updateIdentity({ email: props.creator.email, ...(phone && { phone }) })
  if (identityError.value) {
    return
  }
  isEditPhoneOpen.value = false
  notification.success('Phone updated successfully')
  emits('refresh')
}

const saveClient = async (clientId: number, tierId: number) => {
  const payload: { metapic_client_id?: number; revenue_tier_id?: number } = {}
  const currentClientId = props.creator.client.id
  const currentRevenueTierId = props.creator.revenueTier.id

  if (clientId !== currentClientId) {
    payload.metapic_client_id = clientId
  }
  if (tierId !== currentRevenueTierId) {
    payload.revenue_tier_id = tierId
  }

  if (Object.keys(payload).length === 0) {
    isEditClientOpen.value = false
    return
  }

  const success = await updateCreator(props.creator.id, payload)
  if (!success) {
    return
  }
  isEditClientOpen.value = false
  notification.success('Client updated successfully')
  emits('refresh')
}

const saveTier = async (tierId: number) => {
  const success = await updateCreator(props.creator.id, { revenue_tier_id: tierId })
  if (!success) {
    return
  }
  isEditTierOpen.value = false
  notification.success('Revenue tier updated successfully')
  emits('refresh')
}

const IDENTITY_WARNING =
  'Before making any identity changes, verify that the request comes from the legitimate user by contacting them through an independent channel such as email, phone, or social media other than the one used to submit the request.'
</script>
<template>
  <FlexComponent :gap="0" class="info-layout">
    <BoxComponent class="info-nav">
      <button
        v-for="section in sections"
        :key="section.key"
        class="info-nav__item"
        :class="{ 'info-nav__item--active': activeSection === section.key }"
        @click="activeSection = section.key"
      >
        <TypographyComponent variant="label-large">
          {{ section.label }}
        </TypographyComponent>
      </button>
    </BoxComponent>
    <BoxComponent class="info-content">
      <CreatorDetailGeneralSection
        v-if="activeSection === 'general'"
        :creator="creator"
        :my-page="myPage"
        @edit-username="openEditUsername"
        @edit-email="openEditEmail"
        @edit-phone="openEditPhone"
        @edit-client="isEditClientOpen = true"
        @edit-tier="isEditTierOpen = true"
        @edit-socials="isSocialsDrawerOpen = true"
        @refresh="emits('refresh')"
      />
      <CreatorDetailPaymentSection v-if="activeSection === 'payment'" :extra-info="extraInfo" />
      <CreatorDetailTaxSection v-if="activeSection === 'tax'" :extra-info="extraInfo" />
    </BoxComponent>
  </FlexComponent>
  <EditFieldModal v-model="isEditUsernameOpen" title="Edit username" :is-loading="isUpdating" @save="saveUsername">
    <InputComponent v-model="editUsernameValue" placeholder="Username" />
  </EditFieldModal>
  <EditFieldModal v-model="isEditEmailOpen" title="Edit email" :is-loading="isIdentityUpdating" @save="saveEmail">
    <BoxComponent :mb="16">
      <TagComponent type="warning" size="large" class="identity-warning">
        {{ IDENTITY_WARNING }}
      </TagComponent>
    </BoxComponent>
    <InputComponent v-model="editEmailValue" placeholder="Email address" />
  </EditFieldModal>
  <EditFieldModal v-model="isEditPhoneOpen" title="Edit phone number" :is-loading="isIdentityUpdating" @save="savePhone">
    <BoxComponent :mb="16">
      <TagComponent type="warning" size="large" class="identity-warning">
        {{ IDENTITY_WARNING }}
      </TagComponent>
    </BoxComponent>
    <InputComponent v-model="editPhoneValue" placeholder="+46701234567" />
    <TypographyComponent v-if="phoneValidationError" variant="body-small" color="var(--alert)" class="validation-error">
      {{ phoneValidationError }}
    </TypographyComponent>
  </EditFieldModal>
  <EditClientModal
    v-if="isEditClientOpen"
    v-model="isEditClientOpen"
    :client-id="creator.client.id"
    :client-name="creator.client.name"
    :revenue-tier-id="creator.revenueTier.id"
    :revenue-tier="creator.revenueTier"
    :is-loading="isUpdating"
    @save="saveClient"
  />
  <EditRevenueTierModal
    v-if="isEditTierOpen"
    v-model="isEditTierOpen"
    :client-id="creator.client.id"
    :revenue-tier-id="creator.revenueTierId"
    :is-loading="isUpdating"
    @save="saveTier"
  />
  <CreatorDetailSocialsDrawer v-model="isSocialsDrawerOpen" :creator="creator" @changed="emits('refresh')" />
</template>
<style scoped lang="scss">
.info-layout {
  min-height: 400px;
}
.info-nav {
  width: 160px;
  flex-shrink: 0;
  padding: 16px 0;
  border-right: 1px solid var(--secondary-8);
}
.info-nav__item {
  display: block;
  width: 100%;
  padding: 8px 24px;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  opacity: 0.4;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.7;
  }
  &--active {
    opacity: 1;
  }
}
.info-content {
  flex: 1;
  min-width: 0;
  padding: 16px 0;
}
.validation-error {
  margin-top: 4px;
}
.identity-warning :deep(.el-tag) {
  display: block;
  max-width: 100%;
  white-space: normal;
  line-height: 18px;
  padding: 5px 12px;
  height: auto;
}
</style>
